// operatorevent.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import JapaneseHolidays from 'japanese-holidays';
import "./operatorevent.css";
import useNotificationStore from '../stores/notificationStore';

/* ========================================
 * 定数
 * ======================================== */
const API_URL = import.meta.env.VITE_API_URL;

const DEFAULT_FEES = [
  { class_name: "ベビークラス", price: 5000, sort_order: 1 },
  { class_name: "パピークラス", price: 6000, sort_order: 2 },
  { class_name: "ジュニアクラス", price: 8000, sort_order: 3 },
  { class_name: "インターミディエートクラス", price: 10000, sort_order: 4 },
  { class_name: "オープンクラス", price: 6000, sort_order: 5 },
  { class_name: "チャンピオンクラス", price: 5000, sort_order: 6 },
  { class_name: "ベテランクラス", price: 8000, sort_order: 7 },
];

/* ========================================
 * ヘルパー関数
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, '0');

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
};

const formatInputDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatDateJP = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} 開催`;
};

const formatGender = (g) => {
  if (g === 'M') return '牡';
  if (g === 'F') return '牝';
  return g || '';
};

const formatRegNo = (entry) => {
  if (entry.reg_type === 'JKC') {
    return [entry.reg_no1, entry.reg_no2].filter(Boolean).join('-');
  }
  if (entry.reg_type === 'EXPEDITION') {
    return entry.reg_free || '';
  }
  return '';
};

/* ========================================
 * 日付ピッカーコンポーネント（operator用）
 * ======================================== */
function OpDatePickerField({ value, onChange, placeholder, hasError }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeStartDate, setActiveStartDate] = useState(value ? new Date(value) : new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const displayValue = value ? formatDate(value) : '';

  const handleDateChange = (date) => {
    const formatted = formatInputDate(date);
    onChange({ target: { name: '', value: formatted } });
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name: '', value: '' } });
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || JapaneseHolidays.isHoliday(date)) return 'oe-calSunday';
    if (dayOfWeek === 6) return 'oe-calSaturday';
    return null;
  };

  return (
    <div className="oe-datepicker" ref={containerRef}>
      <div
        className={`oe-datepickerInput ${hasError ? 'is-error' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={displayValue ? '' : 'oe-datepickerPlaceholder'}>
          {displayValue || placeholder || '日付を選択'}
        </span>
        <div className="oe-datepickerActions">
          {value && (
            <button type="button" className="oe-datepickerClear" onClick={handleClear} title="クリア">
              ✕
            </button>
          )}
          <span className="oe-datepickerIcon">📅</span>
        </div>
      </div>

      {isOpen && (
        <div className="oe-datepickerPopup">
          <Calendar
            onChange={handleDateChange}
            value={value ? new Date(value) : null}
            activeStartDate={activeStartDate}
            onActiveStartDateChange={({ activeStartDate: asd }) => setActiveStartDate(asd)}
            view="month"
            minDetail="month"
            formatDay={(locale, date) => date.getDate()}
            formatShortWeekday={(locale, date) => ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]}
            calendarType="gregory"
            tileClassName={getTileClassName}
          />
        </div>
      )}
    </div>
  );
}

/* ========================================
 * メインコンポーネント
 * ======================================== */
export default function OperatorEvent() {
  const queryClient = useQueryClient();

  const [mode, setMode] = useState("list");
  const [editingId, setEditingId] = useState(null);

  const [entriesEventId, setEntriesEventId] = useState(null);
  const [entriesEventName, setEntriesEventName] = useState("");

  const [entrySearchQuery, setEntrySearchQuery] = useState("");
  const [entryClassFilter, setEntryClassFilter] = useState("");
  const [entryGenderFilter, setEntryGenderFilter] = useState("");

  const [entryCurrentPage, setEntryCurrentPage] = useState(1);
  const [entryPageSize, setEntryPageSize] = useState(50);

  const { notification, showNotification } = useNotificationStore();

  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

  const emptyForm = useMemo(() => ({
    event_name: "",
    summary: "",
    venue: "",
    organizer_name: "",
    person_in_charge: "",
    contact_tel: "",
    organizer_email1: "",
    organizer_email2: "",
    notes: "",
    judging: "",
    breed: "",
    event_date: "",
    apply_start_date: "",
    apply_end_date: "",
    max_entries: "",
    list_sending_time: "",
    printing_company_email: "",
    fees: DEFAULT_FEES.map(f => ({ ...f })),
  }), []);

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  /* ========================================
   * イベント一覧取得
   * ======================================== */
  const {
    data: eventsData,
    isLoading: isListLoading,
    isError: isListError,
    error: listError,
  } = useQuery({
    queryKey: ['operator-event-list'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/operator/events/list/`);
      if (!res.data?.success) throw new Error('イベント一覧の取得に失敗しました');
      return res.data.data || {};
    },
    retry: 1,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const events = eventsData?.events || [];

  /* ========================================
   * イベント詳細取得
   * ======================================== */
  const {
    data: eventDetailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useQuery({
    queryKey: ['operator-event-detail', editingId],
    queryFn: async () => {
      if (!editingId) return null;
      const res = await axios.get(`${API_URL}/api/operator/events/get/${editingId}/`);
      if (!res.data?.success) throw new Error('イベント詳細の取得に失敗しました');
      return res.data.data;
    },
    enabled: !!editingId && mode === 'form',
    retry: 1,
    staleTime: 0,
  });

  useEffect(() => {
    if (!eventDetailData) return;
    setFormData({
      event_name: eventDetailData.event_name || "",
      summary: eventDetailData.summary || "",
      venue: eventDetailData.venue || "",
      organizer_name: eventDetailData.organizer_name || "",
      person_in_charge: eventDetailData.person_in_charge || "",
      contact_tel: eventDetailData.contact_tel || "",
      organizer_email1: eventDetailData.organizer_email1 || "",
      organizer_email2: eventDetailData.organizer_email2 || "",
      notes: eventDetailData.notes || "",
      judging: eventDetailData.judging || "",
      breed: eventDetailData.breed || "",
      event_date: eventDetailData.event_date || "",
      apply_start_date: eventDetailData.apply_start_date || "",
      apply_end_date: eventDetailData.apply_end_date || "",
      max_entries: eventDetailData.max_entries || "",
      list_sending_time: eventDetailData.list_sending_time || "",
      printing_company_email: eventDetailData.printing_company_email || "",
      fees: eventDetailData.fees && eventDetailData.fees.length > 0
        ? eventDetailData.fees.map(f => ({ ...f }))
        : DEFAULT_FEES.map(f => ({ ...f })),
    });
    setErrors({});
  }, [eventDetailData]);

  /* ========================================
   * エントリー一覧取得
   * ======================================== */
  const {
    data: entriesData,
    isLoading: isEntriesLoading,
    isError: isEntriesError,
    error: entriesError,
  } = useQuery({
    queryKey: ['operator-event-entries', entriesEventId],
    queryFn: async () => {
      if (!entriesEventId) return null;
      const res = await axios.get(`${API_URL}/api/operator/events/${entriesEventId}/entries/`);
      if (!res.data?.success) throw new Error('エントリー一覧の取得に失敗しました');
      return res.data.data;
    },
    enabled: !!entriesEventId && mode === 'entries',
    retry: 1,
    staleTime: 0,
  });

  const entries = entriesData?.entries || [];

  const filteredEntries = useMemo(() => {
    const q = entrySearchQuery.trim().toLowerCase();
    return entries.filter((entry) => {
      const matchesQuery =
        !q ||
        (entry.pedigree_name || '').toLowerCase().includes(q) ||
        (entry.exhibitor_name || '').toLowerCase().includes(q) ||
        (entry.handler_name || '').toLowerCase().includes(q) ||
        (entry.breed_group || '').toLowerCase().includes(q) ||
        (formatRegNo(entry) || '').toLowerCase().includes(q);
      const matchesClass = !entryClassFilter || entry.class_name === entryClassFilter;
      const matchesGender = !entryGenderFilter || entry.gender === entryGenderFilter;
      return matchesQuery && matchesClass && matchesGender;
    });
  }, [entries, entrySearchQuery, entryClassFilter, entryGenderFilter]);

  const entryClassOptions = useMemo(() => {
    const classes = [...new Set(entries.map((e) => e.class_name).filter(Boolean))];
    return classes.sort();
  }, [entries]);

  const entryTotalItems = filteredEntries.length;
  const entryTotalPages = Math.ceil(entryTotalItems / entryPageSize);
  const entryStartIndex = (entryCurrentPage - 1) * entryPageSize;
  const entryEndIndex = Math.min(entryStartIndex + entryPageSize, entryTotalItems);
  const paginatedEntries = filteredEntries.slice(entryStartIndex, entryEndIndex);

  useEffect(() => {
    setEntryCurrentPage(1);
  }, [entrySearchQuery, entryClassFilter, entryGenderFilter]);

  const handleEntryResetFilters = () => {
    setEntrySearchQuery('');
    setEntryClassFilter('');
    setEntryGenderFilter('');
    setEntryCurrentPage(1);
  };

  const handleEntryPageSizeChange = (newSize) => {
    setEntryPageSize(newSize);
    setEntryCurrentPage(1);
  };

  /* ========================================
   * 更新 Mutation
   * ======================================== */
  const updateMutation = useMutation({
    mutationFn: async ({ eventId, payload }) => {
      const res = await axios.put(`${API_URL}/api/operator/events/update/${eventId}/`, payload);
      if (!res.data?.success) throw new Error(res.data?.message || '更新に失敗しました');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operator-event-list'] });
      queryClient.invalidateQueries({ queryKey: ['operator-event-detail', editingId] });
      showNotification('イベントを更新しました');
      setMode("list");
      setEditingId(null);
    },
    onError: (err) => {
      const message = err.response?.data?.message || err.message || '更新に失敗しました';
      window.alert(message);
    },
  });

  const isSaving = updateMutation.isPending;

  /* ========================================
   * 画面遷移
   * ======================================== */
  const openEdit = (ev) => {
    setEditingId(ev.event_id);
    setErrors({});
    setMode("form");
  };

  const openEntries = (ev) => {
    setEntriesEventId(ev.event_id);
    setEntriesEventName(ev.event_name);
    setEntrySearchQuery('');
    setEntryClassFilter('');
    setEntryGenderFilter('');
    setEntryCurrentPage(1);
    setMode("entries");
  };

  const handleExportExcel = async (eventId) => {
    try {
      const res = await axios.get(`${API_URL}/api/operator/events/${eventId}/entries/excel/`, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
      const filename = match ? decodeURIComponent(match[1]) : `entries_${eventId}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.alert('Excelの出力に失敗しました');
    }
  };

  const handleBack = () => {
    setErrors({});
    setEditingId(null);
    setEntriesEventId(null);
    setEntriesEventName("");
    setMode("list");
  };

  /* ========================================
   * フォーム操作
   * ======================================== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFeeChange = (idx, value) => {
    setFormData((prev) => {
      const next = [...prev.fees];
      next[idx] = {
        ...next[idx],
        price: value === '' ? '' : parseInt(value, 10),
      };
      return { ...prev, fees: next };
    });
  };

  /* ========================================
   * バリデーション
   * ======================================== */
  const validate = () => {
    const newErrors = {};
    if (!formData.event_name.trim()) newErrors.event_name = "イベント名は必須です";
    if (!formData.venue.trim()) newErrors.venue = "開催場所は必須です";
    if (!formData.organizer_name.trim()) newErrors.organizer_name = "主催者名は必須です";
    if (!formData.contact_tel.trim()) newErrors.contact_tel = "連絡先電話番号は必須です";
    if (!formData.organizer_email1.trim()) newErrors.organizer_email1 = "主催者メールアドレス1は必須です";
    if (!formData.event_date) newErrors.event_date = "開催日は必須です";
    if (!formData.apply_start_date) newErrors.apply_start_date = "申込開始日は必須です";
    if (!formData.apply_end_date) newErrors.apply_end_date = "申込終了日は必須です";
    if (
      formData.printing_company_email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.printing_company_email.trim())
    ) {
      newErrors.printing_company_email = "有効なメールアドレスを入力してください";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ========================================
   * 保存
   * ======================================== */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      event_name: formData.event_name,
      summary: formData.summary || null,
      venue: formData.venue,
      organizer_name: formData.organizer_name,
      person_in_charge: formData.person_in_charge || null,
      contact_tel: formData.contact_tel,
      organizer_email1: formData.organizer_email1,
      organizer_email2: formData.organizer_email2 || null,
      notes: formData.notes || null,
      judging: formData.judging || null,
      breed: formData.breed || null,
      event_date: formData.event_date,
      apply_start_date: formData.apply_start_date,
      apply_end_date: formData.apply_end_date,
      max_entries: formData.max_entries ? parseInt(formData.max_entries, 10) : null,
      list_sending_time: formData.list_sending_time || null,
      printing_company_email: formData.printing_company_email.trim() || null,
      fees: formData.fees.map((f, idx) => ({
        event_fee_id: f.event_fee_id,
        class_name: f.class_name,
        price: f.price === '' ? 0 : Number(f.price),
        sort_order: f.sort_order || idx + 1,
      })),
    };

    updateMutation.mutate({ eventId: editingId, payload });
  };

  /* ========================================
   * エントリー一覧画面
   * ======================================== */
  if (mode === "entries") {
    return (
      <div className="opEventPage opEventPage--entries">
        <section className="oe-section oe-section--entries">
          <div className="oe-formHeader oe-entriesHeaderRow">
            <div className="opEntriesTitle">
              {entriesEventName}　─　エントリー一覧
            </div>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => handleExportExcel(entriesEventId)}
            >
              ⬇ Excel出力
            </button>
          </div>

          <div className="oe-filterBar">
            <input
              className="oe-searchInput"
              placeholder="犬名・出陳者名・取扱者名で検索"
              value={entrySearchQuery}
              onChange={(e) => setEntrySearchQuery(e.target.value)}
            />

            <div className="oe-selectWrapper">
              <select
                className="oe-filterSelect"
                value={entryClassFilter}
                onChange={(e) => setEntryClassFilter(e.target.value)}
              >
                <option value="">すべてのクラス</option>
                {entryClassOptions.map((cls) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <div className="oe-selectWrapper">
              <select
                className="oe-filterSelect"
                value={entryGenderFilter}
                onChange={(e) => setEntryGenderFilter(e.target.value)}
              >
                <option value="">すべての性別</option>
                <option value="M">牡</option>
                <option value="F">牝</option>
              </select>
            </div>

            <button type="button" className="oe-ghostButton" onClick={handleEntryResetFilters}>
              条件リセット
            </button>

            <div className="oe-paginationGroup">
              <span className="oe-paginationLabel">表示件数:</span>
              <div className="oe-selectWrapper oe-selectWrapper--small">
                <select
                  className="oe-filterSelect"
                  value={entryPageSize}
                  onChange={(e) => handleEntryPageSizeChange(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <span className="oe-paginationInfo">
                {entryTotalItems > 0
                  ? `${entryStartIndex + 1}-${entryEndIndex} / ${entryTotalItems}`
                  : '0 件'}
              </span>
              <div className="oe-paginationButtons">
                <button
                  className="oe-paginationBtn"
                  onClick={() => setEntryCurrentPage(1)}
                  disabled={entryCurrentPage === 1}
                >|&lt;</button>
                <button
                  className="oe-paginationBtn"
                  onClick={() => setEntryCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={entryCurrentPage === 1}
                >&lt;</button>
                <button
                  className="oe-paginationBtn"
                  onClick={() => setEntryCurrentPage((p) => Math.min(entryTotalPages, p + 1))}
                  disabled={entryCurrentPage >= entryTotalPages}
                >&gt;</button>
                <button
                  className="oe-paginationBtn"
                  onClick={() => setEntryCurrentPage(entryTotalPages)}
                  disabled={entryCurrentPage >= entryTotalPages}
                >&gt;|</button>
              </div>
            </div>
          </div>

          {isEntriesError && (
            <div className="oe-error">
              エラー: {entriesError?.message || 'エントリー一覧の取得に失敗しました'}
            </div>
          )}

          {isEntriesLoading ? (
            <div className="oe-loading">読み込み中...</div>
          ) : entries.length === 0 ? (
            <div className="oe-empty">エントリーはありません。</div>
          ) : (
            <>
              <div className="oe-count">
                {filteredEntries.length !== entries.length
                  ? `${filteredEntries.length} 件 / 全 ${entries.length} 件`
                  : `全 ${entries.length} 件`}
              </div>

              <div className="oe-tableWrapper">
                <table className="oe-table">
                  <thead>
                    <tr>
                      <th>受付No</th>
                      <th>登録番号</th>
                      <th>生年月日</th>
                      <th>性別</th>
                      <th>犬種</th>
                      <th>犬種名/バラエティー</th>
                      <th>出陳クラス</th>
                      <th>ユーザー名</th>
                      <th>メールアドレス</th>
                      <th>電話番号</th>
                      <th>犬名</th>
                      <th>コールネーム</th>
                      <th>父犬名</th>
                      <th>母犬名</th>
                      <th>出陳者名</th>
                      <th>出陳者都道府県</th>
                      <th>出陳取扱者名</th>
                      <th>出陳取扱者都道府県</th>
                      <th>備考</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEntries.map((entry, idx) => (
                      <tr key={entry.entry_id}>
                        <td>{entryStartIndex + idx + 1}</td>
                        <td>{formatRegNo(entry)}</td>
                        <td>{formatDate(entry.birth_date)}</td>
                        <td>{formatGender(entry.gender)}</td>
                        <td>{entry.breed_group}</td>
                        <td>{entry.breed_option}</td>
                        <td>{entry.class_name}</td>
                        <td>{entry.user_name || '—'}</td>
                        <td>{entry.user_email || '—'}</td>
                        <td>{entry.phone || '—'}</td>
                        <td>{entry.pedigree_name}</td>
                        <td>{entry.call_name || '—'}</td>
                        <td>{entry.sire_name || '—'}</td>
                        <td>{entry.dam_name || '—'}</td>
                        <td>{entry.exhibitor_name || '—'}</td>
                        <td>{entry.exhibitor_pref || '—'}</td>
                        <td>{entry.handler_name || '—'}</td>
                        <td>{entry.handler_pref || '—'}</td>
                        <td>{entry.notes || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="oe-footer oe-footer--entries">
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              戻る
            </button>
          </div>
        </section>
      </div>
    );
  }

  /* ========================================
   * フォーム画面
   * ======================================== */
  if (mode === "form") {
    const isFormLoading = isDetailLoading;
    const isFormError = isDetailError;

    return (
      <div className="opEventPage">
        <section className="oe-section">
          <div className="oe-formHeader">
            <h3 className="oe-formTitle">
              イベント編集　※は必須です
            </h3>
          </div>

          {isFormError && (
            <div className="oe-error">
              エラー: {detailError?.message || 'イベント詳細の取得に失敗しました'}
            </div>
          )}

          {isFormLoading ? (
            <div className="oe-loading">読み込み中...</div>
          ) : (
            <form className="oe-form" onSubmit={handleSubmit}>
              {/* イベント名 */}
              <div className="oe-formGroup oe-formGroup--full">
                <label className="oe-label">
                  イベント名 <span className="oe-required">※</span>
                </label>
                <input
                  name="event_name"
                  className={`oe-input ${errors.event_name ? 'is-error' : ''}`}
                  value={formData.event_name}
                  onChange={handleChange}
                  placeholder="イベント名"
                />
                {errors.event_name && <span className="oe-errorText">{errors.event_name}</span>}
              </div>

              {/* 概要 / 開催場所 */}
              <div className="oe-formGrid">
                <div className="oe-formGroup">
                  <label className="oe-label">概要</label>
                  <input
                    name="summary"
                    className="oe-input"
                    value={formData.summary}
                    onChange={handleChange}
                    placeholder="概要"
                  />
                </div>

                <div className="oe-formGroup">
                  <label className="oe-label">
                    開催場所 <span className="oe-required">※</span>
                  </label>
                  <input
                    name="venue"
                    className={`oe-input ${errors.venue ? 'is-error' : ''}`}
                    value={formData.venue}
                    onChange={handleChange}
                    placeholder="開催場所"
                  />
                  {errors.venue && <span className="oe-errorText">{errors.venue}</span>}
                </div>
              </div>

              {/* 主催者 / 担当者 / 連絡先 */}
              <div className="oe-formGrid">
                <div className="oe-formGroup">
                  <label className="oe-label">
                    主催者 <span className="oe-required">※</span>
                  </label>
                  <input
                    name="organizer_name"
                    className={`oe-input ${errors.organizer_name ? 'is-error' : ''}`}
                    value={formData.organizer_name}
                    onChange={handleChange}
                    placeholder="主催者"
                  />
                  {errors.organizer_name && <span className="oe-errorText">{errors.organizer_name}</span>}
                </div>

                <div className="oe-formGroup">
                  <label className="oe-label">担当者</label>
                  <input
                    name="person_in_charge"
                    className="oe-input"
                    value={formData.person_in_charge}
                    onChange={handleChange}
                    placeholder="担当者"
                  />
                </div>

                <div className="oe-formGroup">
                  <label className="oe-label">
                    連絡先電話番号 <span className="oe-required">※</span>
                  </label>
                  <input
                    name="contact_tel"
                    className={`oe-input ${errors.contact_tel ? 'is-error' : ''}`}
                    value={formData.contact_tel}
                    onChange={handleChange}
                    placeholder="連絡先電話番号"
                  />
                  {errors.contact_tel && <span className="oe-errorText">{errors.contact_tel}</span>}
                </div>
              </div>

              {/* メール */}
              <div className="oe-formGrid">
                <div className="oe-formGroup">
                  <label className="oe-label">
                    主催者メールアドレス1 <span className="oe-required">※</span>
                  </label>
                  <input
                    name="organizer_email1"
                    type="email"
                    className={`oe-input ${errors.organizer_email1 ? 'is-error' : ''}`}
                    value={formData.organizer_email1}
                    onChange={handleChange}
                    placeholder="メールアドレス1"
                  />
                  {errors.organizer_email1 && <span className="oe-errorText">{errors.organizer_email1}</span>}
                </div>

                <div className="oe-formGroup">
                  <label className="oe-label">主催者メールアドレス2</label>
                  <input
                    name="organizer_email2"
                    type="email"
                    className="oe-input"
                    value={formData.organizer_email2}
                    onChange={handleChange}
                    placeholder="メールアドレス2"
                  />
                </div>
              </div>

              {/* テキストエリア */}
              <div className="oe-formGroup oe-formGroup--full">
                <label className="oe-label">注意事項</label>
                <textarea
                  name="notes"
                  className="oe-textarea oe-textarea--lg"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="注意事項"
                />
              </div>

              <div className="oe-formGroup oe-formGroup--full">
                <label className="oe-label">審査員</label>
                <textarea
                  name="judging"
                  className="oe-textarea oe-textarea--sm"
                  value={formData.judging}
                  onChange={handleChange}
                  placeholder="審査員"
                />
              </div>

              <div className="oe-formGroup oe-formGroup--full">
                <label className="oe-label">ブリード</label>
                <textarea
                  name="breed"
                  className="oe-textarea oe-textarea--sm"
                  value={formData.breed}
                  onChange={handleChange}
                  placeholder="ブリード"
                />
              </div>

              {/* 開催日と申込期間 */}
              <div className="oe-sectionLabel">開催日と申込期間</div>
              <div className="oe-datesRow">
                <div className="oe-formGroup">
                  <label className="oe-label">
                    開催日 <span className="oe-required">※</span>
                  </label>
                  <OpDatePickerField
                    value={formData.event_date}
                    onChange={(e) => handleDateChange('event_date', e.target.value)}
                    placeholder="年/月/日"
                    hasError={!!errors.event_date}
                  />
                  {errors.event_date && <span className="oe-errorText">{errors.event_date}</span>}
                </div>

                <div className="oe-formGroup">
                  <label className="oe-label">
                    申込開始日 <span className="oe-required">※</span>
                  </label>
                  <OpDatePickerField
                    value={formData.apply_start_date}
                    onChange={(e) => handleDateChange('apply_start_date', e.target.value)}
                    placeholder="年/月/日"
                    hasError={!!errors.apply_start_date}
                  />
                  {errors.apply_start_date && <span className="oe-errorText">{errors.apply_start_date}</span>}
                </div>

                <div className="oe-formGroup">
                  <label className="oe-label">
                    申込終了日 <span className="oe-required">※</span>
                  </label>
                  <OpDatePickerField
                    value={formData.apply_end_date}
                    onChange={(e) => handleDateChange('apply_end_date', e.target.value)}
                    placeholder="年/月/日"
                    hasError={!!errors.apply_end_date}
                  />
                  {errors.apply_end_date && <span className="oe-errorText">{errors.apply_end_date}</span>}
                </div>

                <div className="oe-formGroup">
                  <label className="oe-label">参加上限数</label>
                  <input
                    type="number"
                    name="max_entries"
                    className="oe-input"
                    value={formData.max_entries}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>

              {/* 一覧送付設定 */}
              <div className="oe-sectionLabel">一覧Excel送付設定</div>
              <div className="oe-formGrid">
                <div className="oe-formGroup">
                  <label className="oe-label">一覧Excel送付時間</label>
                  <input
                    type="time"
                    name="list_sending_time"
                    className="oe-input"
                    value={formData.list_sending_time}
                    onChange={handleChange}
                  />
                </div>

                <div className="oe-formGroup">
                  <label className="oe-label">一覧Excel送信先メールアドレス</label>
                  <input
                    type="email"
                    name="printing_company_email"
                    maxLength={320}
                    className={`oe-input ${errors.printing_company_email ? 'is-error' : ''}`}
                    value={formData.printing_company_email}
                    onChange={handleChange}
                    placeholder="一覧Excel送信先メールアドレス"
                  />
                  {errors.printing_company_email && (
                    <span className="oe-errorText">{errors.printing_company_email}</span>
                  )}
                </div>
              </div>

              {/* 出陳料 */}
              <div className="oe-sectionLabel">出陳料</div>
              <div className="oe-feesTable">
                <div className="oe-feesHead">
                  <div className="oe-feesColClass">区分</div>
                  <div className="oe-feesColPrice">価格</div>
                </div>

                {formData.fees.map((fee, idx) => (
                  <div key={idx} className="oe-feesRow">
                    <div className="oe-feesColClass">{fee.class_name}</div>
                    <div className="oe-feesColPrice">
                      <input
                        type="number"
                        className="oe-input"
                        value={fee.price}
                        onChange={(e) => handleFeeChange(idx, e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="oe-footer">
                <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={isSaving}>
                  戻る
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                  {isSaving ? '保存中...' : '更新する'}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    );
  }

  /* ========================================
   * 一覧画面
   * ======================================== */
  return (
    <div className="opEventPage">
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="opEventCard">
        <div className="opEventHeader">
          <div className="opEventTitle">イベント管理</div>
        </div>

        {isListError && (
          <div className="oe-error">
            エラー: {listError?.message || 'イベント一覧の取得に失敗しました'}
          </div>
        )}

        {isListLoading ? (
          <div className="oe-loading">読み込み中...</div>
        ) : events.length === 0 ? (
          <div className="oe-empty">
            貸し出されているイベントはありません。
          </div>
        ) : (
          <div className="opEventList">
            {events.map((ev) => (
              <div key={ev.event_id} className="opEventRow">
                <div className="opEventRowLeft">
                  <div className="opEventName">
                    {ev.event_name}
                    {ev.max_entries && (
                      <span className="opEventMeta">
                        【参加上限 {ev.max_entries}】
                      </span>
                    )}
                  </div>
                  <div className="opEventDate">{formatDateJP(ev.event_date)}</div>
                  <div className="opEventVenue">{ev.venue}</div>
                </div>

                <div className="opEventRowRight">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => openEdit(ev)}
                  >
                    ✎ 編集
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => openEntries(ev)}
                  >
                    ⧉ エントリー一覧
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}